import React, {FC, useCallback, useEffect, useMemo, useRef} from "react";
import {ModalFooterProps, ModalHeaderProps, ModalProps} from "./Modal.types";
import styles from "./Modal.module.scss";
import {Typography} from "../index";
import {CITimes} from "@dua.design/icons";

const Modal: FC<ModalProps> & {
    Footer: FC<ModalFooterProps>;
    Header: FC<ModalHeaderProps>;
} = ({
         open,
         onClose,
         size,
         onCloseAnimationEnd,
         children,
         headless,
         ...rest
     }: ModalProps) => {
    const ref = useRef<HTMLDialogElement>(null);

    const onAnimationEnd = useCallback(() => {
        if (ref.current?.classList.contains("hide")) {
            ref.current?.classList.remove("hide");
            ref.current?.close();
            ref.current?.removeEventListener("animationend", onAnimationEnd);
            onCloseAnimationEnd && onCloseAnimationEnd();
        }
    }, [open]);

    useEffect(() => {
        if (open && !ref.current?.open) {
            try {
                ref.current?.showModal();
            } catch (e) {
                alert("Your browser does not support this feature");
            }
        } else if (!open && ref.current?.open) {
            ref.current?.classList.add("hide");
            ref.current?.addEventListener("animationend", onAnimationEnd);
        }
    }, [open]);

    const classNames = useMemo(() => {
        return [
            styles.Modal,
            styles[size],
            ...(headless ? [styles.headless] : []),
            rest.className
        ].join(" ");
    }, [size, headless, rest]);

    const handleClick = useCallback<React.MouseEventHandler<HTMLDialogElement>>(
        (e) => {
            if (e.target === ref.current) {
                onClose && onClose();
            }
        },
        []
    );

    return (
        <dialog
            {...rest}
            className={classNames}
            data-testid="Modal"
            ref={ref}
            onClick={handleClick}
        >
            {headless ? (
                children
            ) : (
                <>
                    <CITimes size={"20px"} className={styles.closeButton} onClick={onClose}/>
                    <div className={styles.content}>{children}</div>
                </>
            )}
        </dialog>
    );
};

const Footer: FC<ModalFooterProps> = ({children}: ModalFooterProps) => {
    return <div className={styles.ModalFooter}>{children}</div>;
};

Modal.Footer = Footer;

const Header: FC<ModalHeaderProps> = ({
                                          title,
                                          description,
                                      }: ModalHeaderProps) => {
    return (
        <div className={styles.ModalHeader}>
            <Typography variant="h3" token={400} weight={"bold"}>
                {title}
            </Typography>
            <Typography variant="p" token={100} weight={"medium"}>
                {description}
            </Typography>
        </div>
    );
};

Modal.Header = Header;

export default Modal;
